// import { BlendIconName } from "@/components/blendicons";

export const AGENTS_LIMIT = 5;

export const BASE_PROMPT_GENERATION_PROMPT = `
You are an expert AI agent prompt engineer.

Your task:
- Take the user's input, which describes how an AI agent should behave
- Convert it into a clear, complete, production-ready SYSTEM PROMPT for that agent

Rules:
- Output ONLY the final base prompt text
- Do NOT explain your reasoning
- Do NOT ask clarifying questions
- Do NOT provide guidance or suggestions
- Do NOT include phrases like "please provide", "the user should", or "this prompt"
- The output MUST define the agent’s behavior, constraints, and intent
- The output MUST NOT be identical to the user input
- The output MUST NOT be identical to your previous responses
- You may use markdown to better explain the prompt
- Preserve the original intent exactly, but express it more clearly and professionally

Assume the output will be used directly as the agent’s system prompt.
`;
export const GREETINGS_GENERATION_PROMPT = (agentName: string) => `
You are an expert UX copywriter for AI chatbots.

Generate a short, friendly, professional greeting for an AI agent named "${agentName || 'Assistant'}".

Rules:
- Output ONLY the greeting text
- Maximum 200 characters
- Do NOT mention prompts, instructions, or capabilities explicitly
- Do NOT repeat the user input
- The greeting should feel natural and ready for a live chatbot
`;

export const AGENT_TYPE_FALSE_NAME = 'Sequential';
export const AGENT_TYPE_FALSE_DESCRIPTION = 'Fixed steps, no loops. Faster & predictable.';
export const AGENT_TYPE_TRUE_NAME = 'Reasoning';
export const AGENT_TYPE_TRUE_DESCRIPTION = 'Solves complex tasks via self-correction';

export type LLM_Model = {
  id: string;
  name: string;
  value: string;
};

// export type LLM_Model_PROVIDER = {
//   id: string;
//   title: string;
//   icon: BlendIconName;
//   models: LLM_Model[];
// };
// OPENAI = "openai";
// AZURE_OPENAI = "azure";
// ANTHROPIC = "anthropic";
// GOOGLE = "google";
// BEDROCK = "bedrock";
// COHERE = "cohere";
// CUSTOM = "custom";
export const LLM_Model_PROVIDERS_ICON = {
  azure: 'azure',
  meta: 'meta',
  openai: 'openai',
  gemini: 'geimini',
  anthropic: 'anthropic',
  mistral: 'mistral',
  deepseek: 'deepseek',
  openrouter: 'openrouter',
  // custom: "custom",
  // google: "gemini2",
} as const;

// export const getLLMProviderIcon = (provider: string): LLM_Model_PROVIDER["icon"] => {
//   provider = provider.toLowerCase();
//   return LLM_Model_PROVIDERS_ICON[provider as keyof typeof LLM_Model_PROVIDERS_ICON] || "openai";
// };

export const getPng = (provider: string): string => {
  provider = provider.toLowerCase();
  const providerKey = provider as keyof typeof LLM_Model_PROVIDERS_ICON;
  if (providerKey in LLM_Model_PROVIDERS_ICON) return '';
  return `/assets/images/${provider}.png`;
};

export const CHUNKING_STRATEGIES = [
  { label: 'Semantic (Recommended)', value: 'semantic' },
  { label: 'Recursive', value: 'recursive' },
  { label: 'Markdown', value: 'markdown' },
  { label: 'Character', value: 'character' },
];

// export const PREDEIFEND_PROMPTS = [
//   {
//     label: "Customer Support",
//     description:
//       "You are a helpful and empathetic customer support agent. Assist users with their issues, provide clear solutions, and guide them through any problems they face.",
//   },
//   {
//     label: "Feedback",
//     description:
//       "You are a polite assistant focused on gathering user feedback. Encourage honest opinions, listen carefully, and thank users for their input without being defensive.",
//   },
//   {
//     label: "Recruitment",
//     description:
//       "You are a professional recruiter. Engage candidates, answer questions about roles, and collect relevant information in a friendly and respectful manner.",
//   },
//   {
//     label: "Appointment",
//     description:
//       "You are an efficient scheduling assistant. Help users book, reschedule, or cancel appointments while ensuring clarity and convenience.",
//   },
// ];

// export const PREDEFINED_PROMPTS = [
//   { label: "Customer Service", value: "customer_service_agent" },
//   { label: "Lead Generation", value: "lead_generation_agent" },
//   { label: "Enterprise Search", value: "enterprise_search_agent" },
//   { label: "General Agent", value: "general_agent" },
//   { label: "IT Support", value: "it_support_agent" },
//   { label: "HR Assistant", value: "hr_assistant_agent" },
//   { label: "Finance Assistant", value: "finance_assistant_agent" },
//   { label: "Healthcare Assistant", value: "healthcare_assistant_agent" },
//   { label: "Legal Info", value: "legal_info_agent" },
//   { label: "Education Assistant", value: "education_assistant_agent" },
// ] as const;

export const PREDEFINED_PROMPTS = [
  {
    label: 'Customer Service',
    value: 'customer_service_agent',
    description:
      'You are a helpful and empathetic customer support agent. Assist users with their issues, provide clear solutions, and guide them through any problems they face.',
  },
  {
    label: 'Lead Generation',
    value: 'lead_generation_agent',
    description:
      'You are a persuasive lead generation specialist. Engage potential customers, identify their needs, and guide them through the sales funnel effectively.',
  },
  {
    label: 'Enterprise Search',
    value: 'enterprise_search_agent',
    description:
      'You are an intelligent search assistant. Help users find relevant information across enterprise systems quickly and accurately.',
  },
  {
    label: 'General Assistant',
    value: 'general_agent',
    description:
      'You are a versatile general-purpose assistant. Help users with a wide range of tasks, from answering questions to providing recommendations.',
  },
  {
    label: 'IT Support',
    value: 'it_support_agent',
    description:
      'You are a technical IT support specialist. Troubleshoot technical issues, provide step-by-step solutions, and ensure systems run smoothly.',
  },
  {
    label: 'HR Assistant',
    value: 'hr_assistant_agent',
    description:
      'You are a professional HR assistant. Help with recruitment, employee queries, policy questions, and HR-related tasks in a friendly manner.',
  },
  {
    label: 'Finance Assistant',
    value: 'finance_assistant_agent',
    description:
      'You are a knowledgeable finance assistant. Help with financial queries, budget planning, expense tracking, and provide financial guidance.',
  },
  {
    label: 'Healthcare Assistant',
    value: 'healthcare_assistant_agent',
    description:
      'You are a caring healthcare assistant. Provide health information, schedule appointments, and assist with medical queries while maintaining patient confidentiality.',
  },
  {
    label: 'Legal Info',
    value: 'legal_info_agent',
    description:
      'You are a professional legal information assistant. Provide legal information, explain legal concepts, and guide users through legal processes.',
  },
  {
    label: 'Education Assistant',
    value: 'education_assistant_agent',
    description:
      'You are an encouraging education assistant. Help students with learning, answer academic questions, and provide educational guidance.',
  },
] as const;

export enum AgentFormality {
  Formal = 'formal',
  Balanced = 'balanced',
  Casual = 'casual',
}

export const AGENT_FORMALITYS: { label: string; value: AgentFormality }[] = [
  { label: 'Formal', value: AgentFormality.Formal },
  { label: 'Balanced', value: AgentFormality.Balanced },
  { label: 'Casual', value: AgentFormality.Casual },
] as const;

export enum AgentTechnicalLevel {
  Basic = 'basic',
  Intermediate = 'intermediate',
  Advanced = 'advanced',
  Expert = 'expert',
}

export const AGENT_TECHNICAL_DEPTH: { label: string; value: AgentTechnicalLevel }[] = [
  { label: 'Basic', value: AgentTechnicalLevel.Basic },
  { label: 'Intermediate', value: AgentTechnicalLevel.Intermediate },
  { label: 'Advanced', value: AgentTechnicalLevel.Advanced },
  { label: 'Expert', value: AgentTechnicalLevel.Expert },
] as const;

export enum AgentResponseStyle {
  CONCISE = 'concise',
  BALANCED = 'balanced',
  DETAILED = 'detailed',
  PROFESSIONAL = 'professional',
  CONVERSATIONAL = 'conversational',
}

export const AGENT_RESPONSE_STYLES: { label: string; value: AgentResponseStyle }[] = [
  { label: 'Concise', value: AgentResponseStyle.CONCISE },
  { label: 'Balanced', value: AgentResponseStyle.BALANCED },
  { label: 'Detailed', value: AgentResponseStyle.DETAILED },
  { label: 'Professional', value: AgentResponseStyle.PROFESSIONAL },
  { label: 'Conversational', value: AgentResponseStyle.CONVERSATIONAL },
];

export enum AgentBlockSeverityThreshold {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}
export const AGENT_BLOCKS_SEVERITY_THRESHOLD: {
  label: string;
  value: AgentBlockSeverityThreshold;
}[] = [
  { label: 'Low', value: AgentBlockSeverityThreshold.LOW },
  { label: 'Medium (Recommended)', value: AgentBlockSeverityThreshold.MEDIUM },
  { label: 'High', value: AgentBlockSeverityThreshold.HIGH },
  { label: 'Critical', value: AgentBlockSeverityThreshold.CRITICAL },
];

export const LLM_EMBEDDING_MODEL = {
  'text-embedding-ada-002': { name: 'blocks_documents', vector_size: 1536 },
  'text-embedding-3-large': { name: 'blocks_large_documents', vector_size: 3072 },
  'text-embedding-3-small': { name: 'blocks_small_documents', vector_size: 1536 },
};
